import { gsap } from "gsap";
import { Graphics, IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ShowRemoveSceneParams } from "../../ShowRemoveSceneParams";
import { IRemoveBuildingController } from "../../controllers/IRemoveBuildingController";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { BasePageView } from "by/gamefactory/cityversetycoon/scenes/common/components/BasePageView";
import { ButtonWithScaleAndPosYAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAndPosYAnimation";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { StarsResourcePackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/StarsResourcePackage";

export class ConfirmationPage extends BasePageView {
    private readonly commonResources: CommonResourcesPackage;
    private readonly starsResources: StarsResourcePackage;
    private readonly operationControls: PromiseCompletionSource<boolean>;
    private readonly removeBuildingController: IRemoveBuildingController;
    private readonly showRemoveSceneParams: ShowRemoveSceneParams;

    private fadeScreen: FadeScreen;
    private headerText: Text;
    private descriptionText: Text;
    private starIcon: Sprite;
    private removeButton: ButtonWithScaleAnimation;
    private closeButton: ButtonWithScaleAndPosYAnimation;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        canvasScaler: ICanvasScaler,
        commonResources: CommonResourcesPackage,
        starsResources: StarsResourcePackage,
        removeBuildingController: IRemoveBuildingController,
        showRemoveSceneParams: ShowRemoveSceneParams,
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.starsResources = starsResources;
        this.removeBuildingController = removeBuildingController;
        this.showRemoveSceneParams = showRemoveSceneParams;
        this.operationControls = operationControls;
        this.build();
        this.once("added", this.show.bind(this));
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.starIcon = new Sprite(this.starsResources.assets_views_stars_9_png);
        this.starIcon.anchor.set(0.5);
        this.starIcon.angle = -41.53;
        this.addChild(this.starIcon);
        this.headerText = new Text('ARE YOU SURE?', new HeaderTextStyle());
        this.headerText.anchor.set(0.5);
        this.addChild(this.headerText);
        this.descriptionText = new Text('YOUR BUILDING WILL NO LONGER\nBE LISTED ON THE MARKETPLACE', new DescriptionTextStyle());
        this.descriptionText.anchor.set(0.5);
        this.addChild(this.descriptionText);
        this.removeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_orange_png);
        this.removeButton.onUp.add(this.onRemoveButton, this);
        const removeButtonText = new Text('REMOVE', new ButtonTextStyle());
        removeButtonText.position.set(75, 44);
        this.removeButton.defaultView.addChild(removeButtonText);
        this.addChild(this.removeButton);
        this.closeButton = new ButtonWithScaleAndPosYAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.15, -this.closeButton.height * 0.15, this.closeButton.width * 1.25, this.closeButton.height * 1.25);
        this.closeButton.onUp.add(this.onCloseButton, this);
        this.addChild(this.closeButton);
        super.build();
    }

    private async show(): Promise<void> {
        this.removeButton.interactive = false;
        this.closeButton.interactive = false;
        this.fadeScreen.fadeIn();
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.fromTo(this.headerText.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.5, ease: "back.out(2)" });
        animationTimeLine.fromTo(this.starIcon.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.5, ease: "back.out(2)" }, "<50%");
        animationTimeLine.fromTo(this.descriptionText.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.5, ease: "back.out(2)" }, "<");
        animationTimeLine.fromTo(this.removeButton.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.7, ease: "back.out(2)" }, "<");
        animationTimeLine.fromTo(this.closeButton.scale, { x: 0, y: 0 }, { x: 1, y: 1, duration: 0.5, ease: "back.out(2)" }, "<65%");
        await animationTimeLine;
        animationTimeLine.kill();
        this.removeButton.interactive = true;
        this.closeButton.interactive = true;
    }

    protected override layout(width: number, height: number): void {
        this.starIcon.position.set(width * 0.5, 612.08);
        this.headerText.position.set(width * 0.5, 865.56);
        this.descriptionText.position.set(width * 0.5, 1038.68);
        this.removeButton.position.set(240, 1150);
        this.closeButton.position.set(373, 1344);
        super.layout(width, height);
    }

    private async onRemoveButton(): Promise<void> {
        this.removeButton.eventMode = "none";
        const result = await this.removeBuildingController.removeBuildingFromMarketplace(this.showRemoveSceneParams.buildingId, this.showRemoveSceneParams.orderID);
        this.operationControls.complete(result);
    }

    private async onCloseButton(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.closeButton.interactive = false;
        this.removeButton.interactive = false;
        animationTimeLine.delay(0.2);
        await animationTimeLine;
        animationTimeLine.kill();
        this.operationControls.complete(false);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.removeButton.onUp.remove(this.onRemoveButton, this);
        this.closeButton.onUp.remove(this.onCloseButton, this);
        super.destroy(options);
    }
}


class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#fdb731",
                "#ea1a8a"
            ],
            fillGradientType: 1,
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 128
        }));
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.1,
            dropShadowBlur: 6,
            dropShadowDistance: 2,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 78
        }));
    }
}
